import React, { useState } from "react";
import { Download, Mail } from "lucide-react";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import { downloadKundliPdf, emailKundliPdf } from "../lib/kundliPdf";

// Download / email the server-generated Kundli PDF, in English or Hindi.
export const KundliPdfActions = ({ reportId, name, compact = false, onFallback = null, testPrefix = "kundli" }) => {
  const { t, lang: siteLang } = useI18n();
  const { user } = useAuth();
  const [pdfLang, setPdfLang] = useState(siteLang === "hi" ? "hi" : "en");
  const [busy, setBusy] = useState("");
  const [status, setStatus] = useState(null); // { ok, text }
  // Phone-only accounts have no address on file — ask for one before sending.
  const [askEmail, setAskEmail] = useState(false);
  const [emailTo, setEmailTo] = useState("");

  const activeLang = compact ? (siteLang === "hi" ? "hi" : "en") : pdfLang;

  const handleDownload = async () => {
    setStatus(null);
    if (!reportId) { onFallback?.(); return; }
    setBusy("pdf");
    if (activeLang === "hi") setStatus({ ok: true, text: t("pdf.hindiWait") });
    try {
      await downloadKundliPdf(reportId, name, activeLang);
      setStatus(null);
    } catch {
      if (onFallback) { setStatus(null); onFallback(); }
      else setStatus({ ok: false, text: t("glance.pdfFailed") });
    } finally { setBusy(""); }
  };

  const handleEmail = async (override = "") => {
    if (!reportId) return;
    const address = (override || "").trim();
    if (!address && !user?.email) { setAskEmail(true); setStatus(null); return; }
    setStatus(null);
    setBusy("email");
    try {
      const res = await emailKundliPdf(reportId, activeLang, address);
      setStatus({ ok: true, text: `${t("pdf.emailed")} ${res?.email || address}` });
      setAskEmail(false);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setStatus({ ok: false, text: typeof detail === "string" ? detail : t("pdf.emailFailed") });
    } finally { setBusy(""); }
  };

  const emailPrompt = askEmail && (
    <div data-testid={`${testPrefix}-email-prompt`} style={{ display: "flex", gap: "6px", alignItems: "center", flexWrap: "wrap", marginTop: "8px", width: "100%" }}>
      <input data-testid={`${testPrefix}-email-input`} type="email" inputMode="email" value={emailTo}
        onChange={e => setEmailTo(e.target.value)} placeholder="Email to send the PDF to"
        className="cosmic-input" style={{ flex: 1, minWidth: "170px", fontSize: "0.8rem", padding: "8px 10px" }} />
      <button data-testid={`${testPrefix}-email-send-btn`} onClick={() => handleEmail(emailTo)}
        disabled={!!busy || !emailTo.trim()} className="btn-gold"
        style={{ padding: "8px 14px", fontSize: "0.78rem", opacity: (busy || !emailTo.trim()) ? 0.6 : 1 }}>
        {busy === "email" ? "Sending…" : "Send"}
      </button>
    </div>
  );

  if (compact) {
    return (
      <div style={{ display: "flex", gap: "6px", flexShrink: 0, alignItems: "center", flexWrap: "wrap" }}>
        <button data-testid={`${testPrefix}-pdf-btn`} onClick={handleDownload} disabled={!!busy}
          title={t("glance.downloadPdf")} className="btn-ghost"
          style={{ padding: "6px 11px", fontSize: "0.76rem", display: "inline-flex", alignItems: "center", gap: "4px", opacity: busy ? 0.6 : 1 }}>
          <Download size={13} /> {busy === "pdf" ? "…" : "PDF"}
        </button>
        <button data-testid={`${testPrefix}-email-btn`} onClick={() => handleEmail()} disabled={!!busy}
          title={t("pdf.emailBtn")} className="btn-ghost"
          style={{ padding: "6px 11px", fontSize: "0.76rem", display: "inline-flex", alignItems: "center", gap: "4px", opacity: busy ? 0.6 : 1 }}>
          <Mail size={13} /> {busy === "email" ? "…" : (status?.ok ? "✓" : "Email")}
        </button>
        {emailPrompt}
        {status && !status.ok && (
          <span data-testid={`${testPrefix}-pdf-status`} style={{ color: "#fca5a5", fontSize: "0.72rem", width: "100%" }}>{status.text}</span>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "7px", alignItems: "flex-end" }}>
      <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
        <div data-testid={`${testPrefix}-lang-toggle`}
          style={{ display: "flex", borderRadius: "50px", overflow: "hidden", border: "1px solid rgba(255,209,102,0.28)" }}>
          {[["en", t("pdf.langEn")], ["hi", t("pdf.langHi")]].map(([code, label]) => (
            <button key={code} data-testid={`${testPrefix}-lang-${code}`} onClick={() => { setPdfLang(code); setStatus(null); }}
              style={{
                padding: "6px 12px", fontSize: "0.75rem", cursor: "pointer", border: "none",
                background: pdfLang === code ? "rgba(255,209,102,0.18)" : "transparent",
                color: pdfLang === code ? "#FFD166" : "rgba(255,255,255,0.5)",
                fontWeight: pdfLang === code ? 700 : 500, transition: "background-color 0.2s, color 0.2s",
              }}>
              {label}
            </button>
          ))}
        </div>
        <button data-testid={`${testPrefix}-download-pdf-btn`} onClick={handleDownload} disabled={!!busy}
          className="btn-gold" style={{ padding: "9px 16px", fontSize: "0.85rem", display: "inline-flex", alignItems: "center", gap: "6px", opacity: busy ? 0.6 : 1 }}>
          <Download size={14} /> {busy === "pdf" ? t("glance.preparingPdf") : t("glance.downloadPdf")}
        </button>
        {reportId && (
          <button data-testid={`${testPrefix}-email-pdf-btn`} onClick={() => handleEmail()} disabled={!!busy}
            className="btn-ghost" style={{ padding: "9px 14px", fontSize: "0.82rem", display: "inline-flex", alignItems: "center", gap: "6px", opacity: busy ? 0.6 : 1 }}>
            <Mail size={14} /> {busy === "email" ? t("pdf.emailing") : t("pdf.emailBtn")}
          </button>
        )}
      </div>
      {emailPrompt}
      {status && (
        <div data-testid={`${testPrefix}-pdf-status`}
          style={{ fontSize: "0.76rem", color: status.ok ? "#86efac" : "#fca5a5", maxWidth: "320px", textAlign: "right" }}>
          {status.text}
        </div>
      )}
    </div>
  );
};

export default KundliPdfActions;
